import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { IStoreSelectedFacet } from '@generated/graphql'
import { useLocation } from '@reach/router'
import { useSession } from 'src/sdk/session'
import { useFilterResultEvent } from 'src/sdk/analytics/hooks/useFilterResultEvent'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import { graphql } from 'gatsby'
import { scrollToWithFiltered } from 'src/utils/scrollToWithFiltered'
import { normalizeText } from 'src/utils/normalizeText'

import useFilterFacets from './hooks/useFilterFacets'
import type { ValuesObjectProps } from './types'
import type { FilterProps, ActiveFacets, Callback } from './types/filterProps'
import FilterContainerDesktop from './components/FilterContainer/FilterContainerDesktop'
import FilterContainerMobile from './components/FilterContainer/FilterContainerMobile'
import {
  applyFilters,
  applyFilterNew,
  validationCategory,
  getMaxMinAbsolutePrice,
  facetChange,
  facetCleanUp,
  facetsFilteredFormatted,
  jewelryFacetsFilteredFormatted,
  changeAccordionItemMount,
} from './utils'
import { getFacetsFromUrl } from './utils/getFacetsFromUrl'
import { getCookie } from 'src/utils/cookie'

type GroupedFilters = {
  [key: string]: string[]
}

function Filter({
  facets,
  onDismiss,
  isOpen = false,
  setIsOpen,
  testId = 'store-filter',
  term,
  base,
  searchState,
  defaultFacets = [],
  selectedFacets,
  setSelectedFacets,
  sort,
  filterByUrl = true,
  quiz = false,
  filtersExpanded,
}: FilterProps) {
  const [minPriceSelected, setMinPriceSelected] = useState<number>()
  const [maxPriceSelected, setMaxPriceSelected] = useState<number>()
  const [activeFacets, setActiveFacets] = useState<ActiveFacets[]>([])
  const [selectedFacetsFilter, setSelectedFacetsFilter] =
    useState<IStoreSelectedFacet[]>(selectedFacets)

  const [indicesExpanded, setIndicesExpanded] = useState<Set<number>>(
    new Set([])
  )

  const location = useLocation()
  const isJewelryCategory =
    base?.indexOf('/vivara/joias/') !== -1 &&
    selectedFacets?.length === 3 &&
    validationCategory(normalizeText(selectedFacets?.[2]?.value || ''))

  const dismissTransition = useRef<Callback | undefined>()
  const { person } = useSession()
  const { sendFilterResultEvent } = useFilterResultEvent()
  const isNotebook = useWindowDimensions()?.width >= 1279
  const filteredFacets = useFilterFacets({
    facets,
    selectedFacetsFilter,
    term,
    filterByUrl,
    base,
  })

  const uuidToUse = getCookie('uuid')

  useEffect(() => {
    if (filtersExpanded && filteredFacets) {
      filteredFacets.forEach((item: { key: string }, index: number) => {
        if (item.key === filtersExpanded.key) {
          indicesExpanded.clear()
          setIndicesExpanded(new Set(indicesExpanded.add(index)))
        }
      })
    }
  }, [filtersExpanded])

  const facetsFormatted = facetsFilteredFormatted(filteredFacets)
  const jewelryFacetsFormatted = jewelryFacetsFilteredFormatted(filteredFacets)
  const onAccordionChange = useCallback(
    (index: number) => {
      if (indicesExpanded.has(index)) {
        indicesExpanded.delete(index)
        setIndicesExpanded(new Set(indicesExpanded))

        return
      }

      indicesExpanded.clear()

      setIndicesExpanded(new Set(indicesExpanded.add(index)))
    },
    [indicesExpanded]
  )

  useEffect(() => {
    const facetsSelectedUrl = getFacetsFromUrl(window?.location?.href)

    if (!facetsSelectedUrl?.length) {
      setSelectedFacets(defaultFacets)
    } else {
      applyFilters({}, facetsSelectedUrl, setSelectedFacets, sort)
      scrollToWithFiltered(window?.location?.href, true)
    }
  }, [location])

  useEffect(() => {
    setSelectedFacetsFilter(selectedFacets)

    if (!isOpen) {
      setActiveFacets([])
    }
  }, [isOpen, searchState?.state?.selectedFacets, selectedFacets])

  // Opens accordion items with active facets
  useEffect(() => {
    // Ensures all the active facets were identified
    if (activeFacets?.length !== filteredFacets?.length) {
      return
    }

    // Ensures there isn't empty facets
    const selectedActiveFacets = activeFacets?.filter(
      (item) => item?.facets?.length > 0
    )

    // Checks if accordion item is already opened
    isOpen &&
      selectedActiveFacets?.forEach(
        ({ accordionIndex }) =>
          !indicesExpanded?.has(accordionIndex) &&
          onAccordionChange(accordionIndex)
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeFacets])

  const onFacetChange = (item: IStoreSelectedFacet) =>
    facetChange(item, selectedFacetsFilter, setSelectedFacetsFilter)

  const onAccordionItemMount = (index: number, values: ValuesObjectProps[]) =>
    changeAccordionItemMount({
      activeFacets,
      setActiveFacets,
      index,
      values,
      filteredFacets,
    })

  const cleanUpFacets = () => {
    if (!filterByUrl) {
      setSelectedFacets([...defaultFacets])

      indicesExpanded.clear()
    } else {
      facetCleanUp({
        setSelectedFacets,
        setIndicesExpanded,
        setIsOpen,
        defaultFacets,
        term,
        base,
        sort,
      })
    }

    setMinPriceSelected(undefined)
    setMaxPriceSelected(undefined)
    setIsOpen(false)
  }

  const { minAbsolutePrice, maxAbsolutePrice } =
    getMaxMinAbsolutePrice(facetsFormatted)

  const onApply = () => {
    const applyFilterProps = {
      minAbsolutePrice,
      maxAbsolutePrice,
      minPriceSelected,
      maxPriceSelected,
    }

    const groupedFilters: GroupedFilters = selectedFacetsFilter.reduce(
      (acc, item) => {
        if (!acc[item.key]) {
          acc[item.key] = []
        }

        acc[item.key].push(item.value)

        return acc
      },
      {} as GroupedFilters
    )

    const formattedFilters = Object.keys(groupedFilters)
      .map((key) => {
        const values = groupedFilters[key]
        const formattedValue =
          values.length > 1 ? `[${values.join(', ')}]` : `${values[0]}`

        return `${key}: ${formattedValue}`
      })
      .join(', ')

    // eslint-disable-next-line no-useless-escape
    const cleanedFilters = formattedFilters.replace(/\\([\"\\])/g, '$1')

    if (!filterByUrl) {
      applyFilterNew(
        { ...applyFilterProps },
        selectedFacetsFilter,
        setSelectedFacets
      )

      indicesExpanded.clear()
    } else {
      applyFilters(
        { ...applyFilterProps },
        selectedFacetsFilter,
        setSelectedFacets,
        sort
      )

      searchState?.resetInfiniteScroll(0)
      setIndicesExpanded(new Set([]))
    }

    setMinPriceSelected(undefined)
    setMaxPriceSelected(undefined)

    sendFilterResultEvent({
      select_categories: cleanedFilters,
      menu_url: window.location.search,
      user_logged: !!person?.id,
      user_id: person?.id ?? null,
      pageType: `${window.location.origin}${window.location.pathname}`,
      uuid:uuidToUse
    })

    dismissTransition?.current?.()
    setIsOpen(false)
  }

  const defaultFilterContainerProps = {
    testId: `${isNotebook ? 'desktop' : 'mobile'}-${testId}`,
    cleanUpFacets,
    filteredFacets:
      isJewelryCategory && filterByUrl
        ? jewelryFacetsFormatted
        : facetsFormatted,
    indicesExpanded,
    onApply,
    onFacetChange,
    onAccordionChange,
    onAccordionItemMount,
    term: !filterByUrl ? '' : term,
    base: !filterByUrl ? '' : base,
    filterNewTypeStatus: filterByUrl,
    setMinPriceSelected,
    setMaxPriceSelected,
    defaultFacets,
    quiz,
  }

  return (
    <>
      {!isNotebook ? (
        <FilterContainerMobile
          {...defaultFilterContainerProps}
          dismissTransition={dismissTransition}
          isOpen={isOpen}
          onDismiss={() => {
            if (!onDismiss) {
              setIsOpen(false)
              indicesExpanded.clear()

              return
            }

            onDismiss?.()
          }}
          searchState={searchState?.state}
          selectedFacets={selectedFacets}
          selectedFacetsFilter={selectedFacetsFilter}
          setSelectedFacets={setSelectedFacets}
          minPriceSelected={minPriceSelected}
          maxPriceSelected={maxPriceSelected}
        />
      ) : (
        <FilterContainerDesktop
          setIndicesExpanded={setIndicesExpanded}
          {...defaultFilterContainerProps}
          selectedFacets={selectedFacets}
          selectedFacetsFilter={selectedFacetsFilter}
          setSelectedFacets={setSelectedFacets}
          minPriceSelected={minPriceSelected}
          maxPriceSelected={maxPriceSelected}
        />
      )}
    </>
  )
}

export const fragment = graphql`
  fragment Filter_facets on StoreFacet {
    ... on StoreFacetRange {
      key
      label
      min {
        selected
        absolute
      }
      max {
        selected
        absolute
      }
      __typename
    }
    ... on StoreFacetBoolean {
      key
      label
      values {
        label
        value
        selected
        quantity
      }
      __typename
    }
  }
`

export default Filter
